import { enableProdMode, provideZoneChangeDetection } from "@angular/core";
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from "@sentry/angular";
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'chart.js';
import { provideKeycloak } from 'keycloak-angular';
import { Realm } from './app/models/realm';
import { Deserialize } from 'cerialize';

Chart.register(zoomPlugin);

(window as Record<string, any>)['CESIUM_BASE_URL'] = '/assets/cesium/';

window.addEventListener('unhandledrejection', event => {
  if (event.reason === undefined) {
    console.warn('⚠️ Unhandled rejection with undefined'); // plus discret
  }
});

Sentry.init({
  dsn: 'https://e47accbbd203ea7402067657f69fda10@o4507548662038528.ingest.de.sentry.io/4507549501227088',

  ignoreErrors: [
    'fb_xd_fragment',
    'Script error.',
    'Script error',
    /ResizeObserver loop (limit|completed)/,
    /^chrome-extension:/,
    /^moz-extension:/,
    'NetworkError when attempting to fetch resource.',
    'WebSocket is already in CLOSING or CLOSED state',
    /Handled unknown error/,
    'Non-Error exception: undefined or null',

    // Cesium RequestScheduler
    /Request cancelled:/,
  ],

  /**
   * Dernière sécurité pour supprimer les "Request cancelled"
   * avant envoi à Sentry.
   */
  beforeSend(event, hint) {
    const originalException = hint?.originalException as any;

    const messages = [
      originalException?.message,
      event.message,
      ...(event.exception?.values?.map(value => value.value) ?? []),
    ]
      .filter(Boolean)
      .join(' ');

    if (messages.includes('Request cancelled:')) {
      return null;
    }

    return event;
  },

  denyUrls: [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /.*\/ads\/.*/,
    /.*doubleclick\.net.*/,
    /.*google-analytics\.com.*/,
    /.*fbcdn\.net.*/,
  ],

  integrations: [
    Sentry.browserTracingIntegration({
      shouldCreateSpanForRequest: (url) => {
        const ignored = [
          'assets.ion.cesium.com',
          'api.cesium.com',
          '/tilesets/tiles/',
          '/tiles/model/',
        ];

        return !ignored.some(pattern => url.includes(pattern));
      },
    }),

    Sentry.replayIntegration(),
  ],

  enabled: environment.production,
  debug: !environment.production,

  // Performance Monitoring
  tracesSampleRate: environment.production ? 0.2 : 1.0,

  // Session Replay
  replaysSessionSampleRate: environment.production ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,
});

async function loadRealm(): Promise<Realm> {
  const response = await fetch(environment.apiUrl + '/realm/');

  if (!response.ok) {
    throw new Error(
      `Impossible de charger la configuration Keycloak: ${response.status}`
    );
  }

  const data = await response.json();

  return Deserialize(data, Realm);
}

async function bootstrap(): Promise<void> {
  const realm = await loadRealm();

  await platformBrowserDynamic().bootstrapModule(AppModule, {
    applicationProviders: [
      provideZoneChangeDetection(),
      provideKeycloak({
        config: {
          url: realm.url,
          realm: realm.name,
          clientId: realm.clientId
        },
        initOptions: {
          onLoad: 'login-required'
        }
      })
    ]
  });
}

if (environment.production) {
  enableProdMode();
}

bootstrap().catch(error => {
  console.error('Échec du démarrage de l’application', error);
});
